import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { Notification, NotificationLevel } from 'src/app/model/notification';
import { ScheduleService } from 'src/app/services/schedule.service';
import * as ScheduleActions from './schedule.actions';
import * as NotificationsActions from '../notifications/notifications.actions';
import { AppState } from '../app.reducer';
import { Store } from '@ngrx/store';
import * as AnalyticsActions from 'src/app/store/analytics/analytics.actions';

@Injectable()
export class ScheduleEffects {
    getSchedules$ = createEffect(() => this.actions$.pipe(
        ofType(ScheduleActions.getSchedules),
        switchMap((action) => this.scheduleService.getSchedules(null, action.siteId).pipe(
                map((resp) => {
                    if (resp.Success) {
                        return ScheduleActions.getSchedulesSuccess({ schedules: resp.Data });
                    } else {
                        return ScheduleActions.getSchedulesFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(ScheduleActions.getSchedulesFailed({ errorMessage: error.message })))
            )
        )
    ));

    addSchedule$ = createEffect(() => this.actions$.pipe(
        ofType(ScheduleActions.addSchedule),
        switchMap((action) => this.scheduleService.addSchedule(action.schedule).pipe(
                map((resp) => {
                    if (resp.Success) {
                        return ScheduleActions.addScheduleSuccess({
                            message: this.translate.instant('SCHEDULE_CREATED_SUCCESS'),
                            schedule: { ...action.schedule, Id: resp.Data.Id }
                        });
                    } else {
                        return ScheduleActions.addScheduleFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(ScheduleActions.addScheduleFailed({ errorMessage: error.message })))
            )
        )
    ));

    addScheduleSuccessAnalytics$ = createEffect(() => this.actions$.pipe(
        ofType(ScheduleActions.addScheduleSuccess),
        tap(() => {
            setTimeout(() => {
                this.store.dispatch(AnalyticsActions.eventNewAction({ eventName: 'new_schedule_completed', params: { page_title: 'Scenarios | Schedules' } }));
            }, 1000);
        }),
    ), { dispatch: false });

    editSchedule$ = createEffect(() => this.actions$.pipe(
        ofType(ScheduleActions.editSchedule),
        switchMap((action) => this.scheduleService.editSchedule(action.schedule).pipe(
                map((resp) => {
                    if (resp.Success) {
                        return ScheduleActions.editScheduleSuccess({
                            message: this.translate.instant('SCHEDULE_EDITED_SUCCESS'),
                            schedule: action.schedule
                        });
                    } else {
                        return ScheduleActions.editScheduleFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(ScheduleActions.editScheduleFailed({ errorMessage: error.message })))
            )
        )
    ));

    editScheduleSuccessAnalytics$ = createEffect(() => this.actions$.pipe(
        ofType(ScheduleActions.editScheduleSuccess),
        tap(() => {
            setTimeout(() => {
                this.store.dispatch(AnalyticsActions.eventNewAction({ eventName: 'edit_schedule_completed', params: { page_title: 'Scenarios | Schedules' } }));
            }, 1000);
        }),
    ), { dispatch: false });

    deleteSchedule$ = createEffect(() => this.actions$.pipe(
        ofType(ScheduleActions.deleteSchedule),
        switchMap((action) => this.scheduleService.deleteSchedule(action.scheduleId).pipe(
                map((resp) => {
                    if (resp.Success) {
                        return ScheduleActions.deleteScheduleSuccess({
                            message: this.translate.instant('SCHEDULE_DELETED_SUCCESS'),
                            scheduleId: action.scheduleId
                        });
                    } else {
                        return ScheduleActions.deleteScheduleFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(ScheduleActions.deleteScheduleFailed({ errorMessage: error.message })))
            )
        )
    ));

    deleteScheduleSuccessAnalytics$ = createEffect(() => this.actions$.pipe(
        ofType(ScheduleActions.deleteScheduleSuccess),
        tap(() => {
            setTimeout(() => {
                this.store.dispatch(AnalyticsActions.eventNewAction({ eventName: 'delete_schedule_completed', params: { page_title: 'Scenarios | Schedules' } }));
            }, 1000);
        }),
    ), { dispatch: false });

    notifySuccess$ = createEffect(() => this.actions$.pipe(
        ofType(
            ScheduleActions.addScheduleSuccess,
            ScheduleActions.editScheduleSuccess,
            ScheduleActions.deleteScheduleSuccess,
        ),
        tap((action) => {
            if (action.message === '') {
                return;
            }
            const notification = new Notification("SCHEDULE", action.message, NotificationLevel.Success, '');
            this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));
        })
    ), { dispatch: false });

    notifyError$ = createEffect(() => this.actions$.pipe(
        ofType(
            ScheduleActions.addScheduleFailed,
            ScheduleActions.editScheduleFailed,
            ScheduleActions.deleteScheduleFailed,
        ),
        tap((action) => {
            if (action.errorMessage === '') {
                return;
            }
            const notification = new Notification("SCHEDULE", action.errorMessage, NotificationLevel.Error, '');
            this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));
        })
    ), { dispatch: false });

    constructor(
        private actions$: Actions,
        private scheduleService: ScheduleService,
        private translate: TranslateService,
        private store: Store<AppState>,
    ) { }
}
